import React from 'react';

/**
 * LoadingSpinner — clean, on-brand loader for user data pages.
 * A single elegant dual-ring with a soft glow and a lock mark, plus a
 * three-dot pulse. Deliberately lighter than the old particle/progress-bar
 * version so it feels fast and modern on both web and the Android APK.
 */
const LoadingSpinner = ({ message = "Loading your secure data..." }) => {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0a0a0f] px-6">
      <div className="flex flex-col items-center text-center">
        {/* Ring loader */}
        <div className="relative w-20 h-20 mb-7">
          {/* Ambient glow */}
          <div className="absolute inset-0 rounded-full bg-[#3E7BFA]/20 blur-2xl" />

          {/* Track */}
          <div className="absolute inset-0 rounded-full border-[3px] border-white/5" />

          {/* Spinning arc */}
          <div className="absolute inset-0 rounded-full border-[3px] border-transparent border-t-[#3E7BFA] border-r-[#3E7BFA] animate-spin" />

          {/* Lock mark */}
          <div className="absolute inset-0 flex items-center justify-center">
            <svg className="w-7 h-7 text-[#5B8DEF]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
        </div>

        {/* Message */}
        <p className="text-base sm:text-lg font-semibold text-white mb-3">{message}</p>

        {/* Dot pulse */}
        <div className="flex items-center gap-1.5 mb-5">
          {[0, 1, 2].map((i) => (
            <span
              key={i}
              className="w-1.5 h-1.5 rounded-full bg-[#3E7BFA]"
              style={{ animation: 'ls-pulse 1.1s cubic-bezier(0.22, 1, 0.36, 1) infinite', animationDelay: `${i * 160}ms` }}
            />
          ))}
        </div>

        {/* Encrypted badge */}
        <div className="inline-flex items-center gap-2 bg-white/5 border border-white/10 px-3.5 py-1.5 rounded-full">
          <svg className="w-4 h-4 text-green-400" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
          <span className="text-xs text-gray-400 font-medium">End-to-end encrypted</span>
        </div>
      </div>

      <style>{`
        @keyframes ls-pulse {
          0%, 100% { transform: scale(0.7); opacity: 0.4; }
          40% { transform: scale(1); opacity: 1; }
        }
        @media (prefers-reduced-motion: reduce) {
          .animate-spin { animation-duration: 1.6s; }
        }
      `}</style>
    </div>
  );
};

export default LoadingSpinner;
